import { ActionWithPayload } from '@/types/redux';
import { combineActions, handleActions } from 'redux-actions';
import { createSelector, Reducer } from '@reduxjs/toolkit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getFollowSellerAnalytics, getUnfollowSellerAnalytics } from './analytics';
import { GlobalState } from '../rootReducer';
import { LOAD_BIDDER_DATA_SUCCESS, LOG_OUT_BIDDER, LOGIN_SUCCESS } from './actions';
import { PaginationFilter } from '@liveauctioneers/caterwaul-components/types/PaginationFilter';
import { PASSWORD_RESET_SUCCESS } from './passwordReset';
import { sellerSortOrders } from '@/enums';
import api from '@/redux/api/seller';
import difference from 'lodash/difference';
import union from 'lodash/union';

/* Action Types */
export const FOLLOW_SELLER_FAIL = 'la/user/followSeller/FAIL';
export const FOLLOW_SELLER_REQUEST = 'la/user/followSeller/REQUEST';
export const FOLLOW_SELLER_SUCCESS = 'la/user/followSeller/SUCCESS';

export const UNFOLLOW_SELLER_FAIL = 'la/user/unfollowSeller/FAIL';
export const UNFOLLOW_SELLER_REQUEST = 'la/user/unfollowSeller/REQUEST';
export const UNFOLLOW_SELLER_SUCCESS = 'la/user/unfollowSeller/SUCCESS';

export const FOLLOW_SELLER_FILTER_CHANGE = 'FOLLOW_SELLER_FILTER_CHANGE';
export const FOLLOW_SELLER_FILTER_CHANGE_FAIL = 'FOLLOW_SELLER_FILTER_CHANGE_FAIL';

// reducer
export const DEFAULT_STATE = {
    followedSellerFilters: {
        keyword: '',
        page: 1,
        pageSize: 24,
        sort: sellerSortOrders.NAME_LOWEST_FIRST,
    },
    followedSellerIds: [],
};

export type State = {
    followedSellerFilters: PaginationFilter;
    followedSellerIds: number[];
};

export const followSeller: Reducer<State> = handleActions(
    {
        [FOLLOW_SELLER_FILTER_CHANGE]: (state: State, action: ActionWithPayload<any>) => {
            return {
                ...state,
                followedSellerFilters: { ...state.followedSellerFilters, ...action.payload },
            };
        },
        [FOLLOW_SELLER_SUCCESS]: (state: State, action: ActionWithPayload<any>) => {
            return {
                ...state,
                followedSellerIds: union(state.followedSellerIds, [action.payload]),
            };
        },
        [LOG_OUT_BIDDER]: () => {
            return DEFAULT_STATE;
        },
        [combineActions(LOAD_BIDDER_DATA_SUCCESS, LOGIN_SUCCESS, PASSWORD_RESET_SUCCESS)]: (
            state: State,
            action: ActionWithPayload<any>
        ) => {
            return {
                ...state,
                followedSellerIds: [...action.payload.followedSellers],
            };
        },
        [UNFOLLOW_SELLER_SUCCESS]: (state: State, action: ActionWithPayload<any>) => {
            return {
                ...state,
                followedSellerIds: difference(state.followedSellerIds, [action.payload]),
            };
        },
    },
    DEFAULT_STATE
);

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.followSeller;
const idSelector = (state: GlobalState, id) => id;

export const getFollowedSellerIds = createSelector(stateSelector, (state: State) => [...state.followedSellerIds]);

export const getFollowedSellerFilters = createSelector(stateSelector, (state: State) => state.followedSellerFilters);

// TODO: update endpoint to return total followed sellers
export const getTotalFollowedSellerCount = createSelector(
    stateSelector,
    (state: State) => state.followedSellerIds.length
);

export const isSellerFollowed: (arg0: any, arg1: number) => boolean = createSelector(
    [getFollowedSellerIds, idSelector],
    (followedSellerIds, id) => followedSellerIds.includes(id)
);

/* ACTION CREATORS */
export const onFollowSellerFilterChange = (filters: PaginationFilter) => async (dispatch: Function) => {
    try {
        dispatch({
            payload: filters,
            type: FOLLOW_SELLER_FILTER_CHANGE,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error.message,
            type: FOLLOW_SELLER_FILTER_CHANGE_FAIL,
        });
    }
};

export const submitFollowSeller = (sellerId: number) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: FOLLOW_SELLER_REQUEST,
        });

        await api.postFollow({ authToken, deployment, sellerId });
        return dispatch({
            meta: getFollowSellerAnalytics(state, sellerId),
            payload: sellerId,
            type: FOLLOW_SELLER_SUCCESS,
        });
    } catch (error) {
        return dispatch({
            error: true,
            payload: error,
            type: FOLLOW_SELLER_FAIL,
        });
    }
};

export const submitUnfollowSeller = (sellerId: number) => async (dispatch: Function, getState: Function) => {
    try {
        const state = getState();
        const authToken = getAuthToken(state);
        const deployment = getDeployment(state);
        dispatch({
            type: UNFOLLOW_SELLER_REQUEST,
        });
        await api.postUnfollow({ authToken, deployment, sellerId });
        dispatch({
            meta: getUnfollowSellerAnalytics(state, sellerId),
            payload: sellerId,
            type: UNFOLLOW_SELLER_SUCCESS,
        });
    } catch (error) {
        dispatch({
            error: true,
            payload: error,
            type: UNFOLLOW_SELLER_FAIL,
        });
    }
};
